<template>
  <div class="mt-4 grid gap-4 md:grid-cols-3">
    <div class="col-span-2 grid gap-x-4 gap-y-2 md:grid-cols-2">
      <div class="flex flex-col gap-2">
        <SelectField
          name="pickupLocationType"
          :options="locationTypeOptions"
          :label="t('startLocation')"
          :disabled="pickupDisabled"
        />

        <SelectField
          v-if="formValues.pickupLocationType === BookingLocationType.STATION"
          name="pickupStationId"
          :options="stationOptions"
          :disabled="pickupDisabled"
          data-testid="select-pickup-station"
        />

        <SearchLocation
          v-else
          auto-confirm-on-select
          :location="formValues.pickupLocation"
          :disabled="pickupDisabled"
          @update:location="
            setFieldValue('pickupLocation', {
              coordinates: $event?.coordinates ?? null,
              address: $event?.address ?? null,
            })
          "
        />
      </div>

      <div class="flex flex-col gap-2">
        <SelectField
          name="dropoffLocationType"
          :options="locationTypeOptions"
          :label="t('endLocation')"
          :disabled="dropoffDisabled"
        />

        <SelectField
          v-if="formValues.dropoffLocationType === BookingLocationType.STATION"
          name="dropoffStationId"
          :options="stationOptions"
          :disabled="dropoffDisabled"
        />

        <SearchLocation
          v-else
          auto-confirm-on-select
          :location="formValues.dropoffLocation"
          data-testid="search-dropoff-location-input"
          :disabled="dropoffDisabled"
          @update:location="
            setFieldValue('dropoffLocation', {
              coordinates: $event?.coordinates ?? null,
              address: $event?.address ?? null,
            })
          "
        />
      </div>
    </div>
  </div>
  <slot name="after" />
  <div v-if="isFormDirty" class="mt-4 flex justify-end gap-4">
    <CVButton
      :is-loading="isBusy"
      :disabled="!readyToSave"
      variant="default"
      @click.prevent="onSave"
    >
      {{ t('save') }}
    </CVButton>
    <CVButton :disabled="isBusy" variant="secondary" @click.prevent="resetForm">
      {{ t('cancel') }}
    </CVButton>
  </div>
</template>

<script lang="ts" setup>
import { BookingLocationType } from '@/entities/bookings/booking-location-type.enum';
import type { Booking } from '@/entities/bookings/booking.entity';
import type { UpdateBookingDto } from '@/entities/bookings/booking.entity';
import { useI18n } from 'vue-i18n';
import { useStations } from '@/queries/use-stations';
import { computed, ref, watch } from 'vue';
import SearchLocation from './SearchLocation.vue';
import { pointToLocationDto } from '@/hooks/use-location-to-coordinates';
import { useLocalizedFields } from '@/hooks/use-localized-fields';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import CVButton from '@/components/buttons/CVButton.vue';
import { useForm, useFormContext, useIsFormDirty } from 'vee-validate';
import SelectField from '@/components/SelectField.vue';
import { PriceRecalculationMode } from '@carvia/ros-client-types';
import { useTransferPricePreview, type PricePreview } from '@/hooks/use-transfer-price-preview';
import { getTotalTransferPrice } from '@/utils/get-total-transfer-price';
import { usePriceChangeConfirmation } from '@/components/price-change-confirmation/price-change-confirmation';

const props = defineProps<{
  booking: Booking;
  pickupDisabled?: boolean;
  dropoffDisabled?: boolean;
}>();
const emit = defineEmits<{
  (e: 'hasChanged', value: boolean): void;
  (e: 'change-price-preview', value: PricePreview): void;
}>();

const { t } = useI18n();
const { getLocalizedField } = useLocalizedFields();

const initialValues = {
  pickupLocationType: props.booking.pickupLocationType,
  dropoffLocationType: props.booking.dropoffLocationType,
  pickupStationId: props.booking.pickupStation?.id,
  dropoffStationId: props.booking.dropoffStation?.id,
  pickupLocation: {
    coordinates: pointToLocationDto(props.booking.pickupLocation),
    address: props.booking.pickupLocationGeocodedAddress,
  },
  dropoffLocation: {
    coordinates: pointToLocationDto(props.booking.dropoffLocation),
    address: props.booking.dropoffLocationGeocodedAddress,
  },
};

const { values: parentValues } = useFormContext<UpdateBookingDto>();

const {
  handleSubmit,
  values: formValues,
  setFieldValue,
  resetForm,
} = useForm({
  initialValues,
});
const isFormDirty = useIsFormDirty();

const { confirmAndSave, isBusy } = usePriceChangeConfirmation();

const readyToSave = computed(
  () =>
    !(
      (formValues.pickupLocationType === BookingLocationType.STREET &&
        !formValues.pickupLocation?.coordinates) ||
      (formValues.dropoffLocationType === BookingLocationType.STREET &&
        !formValues.dropoffLocation?.coordinates)
    ),
);

watch(
  () => formValues.pickupLocationType,
  (type, previous) => {
    if (!previous) return;
    if (type === BookingLocationType.STATION) {
      setFieldValue('pickupLocation', { coordinates: null, address: null });
    } else {
      setFieldValue('pickupStationId', undefined);
    }
  },
);
watch(
  () => formValues.dropoffLocationType,
  (type, previous) => {
    if (!previous) return;
    if (type === BookingLocationType.STATION) {
      setFieldValue('dropoffLocation', { coordinates: null, address: null });
    } else {
      setFieldValue('dropoffStationId', undefined);
    }
  },
);

const onSave = handleSubmit(async (values) => {
  const updateBookingDto: UpdateBookingDto = {
    pricing: {
      priceRecalculationMode: PriceRecalculationMode.SCALE,
    },
    pickupLocationType: values.pickupLocationType,
    pickupStationId: values.pickupStationId,
    pickupLocation: values.pickupLocation.coordinates,
    pickupLocationGeocodedAddress: values.pickupLocation.address,
    dropoffLocationType: values.dropoffLocationType,
    dropoffStationId: values.dropoffStationId,
    dropoffLocation: values.dropoffLocation.coordinates,
    dropoffLocationGeocodedAddress: values.dropoffLocation.address,
  };
  const { status } = await confirmAndSave({
    booking: props.booking,
    updateBookingDto,
    allowKeepOldPrice: true,
  });
  if (status === 'saved') resetForm({ values });
});

const { data: stationsData } = useStations(500);
const stations = useFlattenPaginatedData(stationsData);

const locationTypeOptions = computed(() =>
  Object.entries(BookingLocationType).map(([key, value]) => ({
    label: t(`locationTypes.${key}`),
    value,
  })),
);

const stationOptions = computed(() =>
  stations.value?.map((station) => ({
    label: getLocalizedField(station.info),
    value: station.id,
  })),
);

watch(isFormDirty, () => emit('hasChanged', isFormDirty.value));

const hasPickupSelection = () =>
  (formValues.pickupLocationType === BookingLocationType.STREET &&
    !!formValues.pickupLocation?.coordinates) ||
  (formValues.pickupLocationType === BookingLocationType.STATION && !!formValues.pickupStationId);

const hasDropoffSelection = () =>
  (formValues.dropoffLocationType === BookingLocationType.STREET &&
    !!formValues.dropoffLocation?.coordinates) ||
  (formValues.dropoffLocationType === BookingLocationType.STATION && !!formValues.dropoffStationId);

const getPricing = () => {
  return {
    ...(parentValues.pricing ?? {}),
    priceRecalculationMode: PriceRecalculationMode.SCALE,
  };
};

const { previewTransferPrice, previewTotalPrice, isLoadingTransferPrice } = useTransferPricePreview(
  {
    booking: props.booking,
    watchSource: () => ({
      startDate: parentValues.startDate,
      endDate: parentValues.endDate,
      pickupLocationType: formValues.pickupLocationType,
      pickupStationId: formValues.pickupStationId,
      pickupLocation: formValues.pickupLocation,
      dropoffLocationType: formValues.dropoffLocationType,
      dropoffStationId: formValues.dropoffStationId,
      dropoffLocation: formValues.dropoffLocation,
    }),
    shouldPreview: () => hasPickupSelection() && hasDropoffSelection(),
    buildBookingDto: () => ({
      ...parentValues,
      pickupLocationType: formValues.pickupLocationType,
      pickupStationId: formValues.pickupStationId,
      pickupLocation: formValues.pickupLocation.coordinates,
      pickupLocationGeocodedAddress: formValues.pickupLocation.address,
      dropoffLocationType: formValues.dropoffLocationType,
      dropoffStationId: formValues.dropoffStationId,
      dropoffLocation: formValues.dropoffLocation.coordinates,
      dropoffLocationGeocodedAddress: formValues.dropoffLocation.address,
      pricing: getPricing(),
    }),
    getTransferPrice: getTotalTransferPrice,
    watchDeep: true,
  },
);

watch(
  [previewTransferPrice, previewTotalPrice, isLoadingTransferPrice],
  () =>
    emit('change-price-preview', {
      transferPrice: previewTransferPrice.value,
      totalPrice: previewTotalPrice.value,
      isLoading: isLoadingTransferPrice.value,
    }),
  { immediate: true },
);
</script>

<i18n lang="json">
{
  "en": {
    "startLocation": "Start Location",
    "endLocation": "End Location"
  },
  "de": {
    "startLocation": "Startort",
    "endLocation": "Endort"
  },
  "es": {
    "startLocation": "Lugar de inicio",
    "endLocation": "Lugar de fin"
  }
}
</i18n>
